import { database } from './database'
import { useAuth } from '../contexts/AuthContext'
import { loadState as loadLocalState, saveState as saveLocalState } from './storage'

// Enhanced storage that uses Supabase when available, falls back to localStorage
export const useSupabaseStorage = () => {
  const { user, isConfigured } = useAuth()

  // Add a simple debouncing mechanism
  let saveTimeout = null

  const loadState = async () => {
    if (isConfigured && user) {
      try {
        // Load from Supabase
        const profile = await database.getUserProfile(user.id)
        const entries = await database.getEntries(user.id)

        if (profile) {
          return {
            onboardingDone: profile.onboarding_done,
            utopia: profile.utopia || { earth: "", water: "", air: "", fire: "" },
            theme: profile.theme || "Stoicism",
            entries: entries.map(entry => ({
              date: entry.date,
              values: entry.values
            })),
            streakGoal: profile.streak_goal || false,
            cycleTracking: profile.cycle_tracking || undefined,
            ratingTracking: profile.rating_tracking || undefined
          }
        }
      } catch (error) {
        console.error('Error loading from Supabase, falling back to localStorage:', error)
        // Fall through to localStorage fallback
      }
    }

    // Fallback to localStorage
    return loadLocalState()
  }

  const saveState = async (state) => {
    // Don't save if we don't have a user or if state is empty/default
    if (!user || !state) {
      return
    }

    // Clear any existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout)
    }

    // Debounce the save operation
    saveTimeout = setTimeout(async () => {
      if (isConfigured && user) {
        // Save to Supabase
        try {
          await database.updateUserProfile(user.id, {
            onboarding_done: state.onboardingDone,
            utopia: state.utopia,
            theme: state.theme,
            streak_goal: state.streakGoal,
            cycle_tracking: state.cycleTracking || null,
            rating_tracking: state.ratingTracking || null
          })

          // Save all entries
          if (state.entries && state.entries.length > 0) {
            for (const entry of state.entries) {
              await database.saveEntry(user.id, {
                date: entry.date,
                values: entry.values
              })
            }
          }
        } catch (error) {
          console.error('Error saving to Supabase:', error)
          // Fallback to localStorage
          saveLocalState(state)
        }
      } else {
        // Fallback to localStorage
        saveLocalState(state)
      }
    }, 1000) // 1 second debounce
  }

  const saveEntry = async (entry) => {
    if (isConfigured && user) {
      try {
        return await database.saveEntry(user.id, entry)
      } catch (error) {
        console.error('Error saving entry:', error)
        return null
      }
    }
    return null
  }

  const getEntries = async () => {
    if (isConfigured && user) {
      try {
        const entries = await database.getEntries(user.id)
        return entries.map(entry => ({
          date: entry.date,
          values: entry.values
        }))
      } catch (error) {
        console.error('Error fetching entries:', error)
        return []
      }
    }
    return []
  }

  return {
    loadState,
    saveState,
    saveEntry,
    getEntries,
    isSupabaseAvailable: isConfigured && user
  }
}
